/**
 * What the durable pipeline has actually run, stage by stage.
 *
 * `fastapi_worker_jobs` is the live queue - thirty-two thousand rows of real
 * generation work - and until now no admin screen read it. The list answers
 * "what ran and what broke", the detail answers "where did this one break and
 * with what", and the three controls are the working replacements for the
 * `retry_task`/`cancel_pipeline` operator controls, which wrote to an unused
 * table and reported success regardless.
 *
 * The detail reads a job as the steps it ran - one card per step per attempt,
 * with that step's inputs, outputs and log lines - rather than as the raw
 * checkpoints the worker wrote. How a card is assembled from checkpoints and
 * the run log is in `./workflow-steps`; the raw sequence stays available in
 * a collapsed technical timeline.
 */

import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router';
import {
  cancelJobApiV1InternalOperatorWorkflowJobsJobsJobIdCancelPost,
  getJobApiV1InternalOperatorWorkflowJobsJobsJobIdGet,
  listJobsApiV1InternalOperatorWorkflowJobsJobsGet,
  listWorkflowFamiliesApiV1InternalOperatorWorkflowJobsWorkflowsGet,
  resumeJobApiV1InternalOperatorWorkflowJobsJobsJobIdResumePost,
  retryJobApiV1InternalOperatorWorkflowJobsJobsJobIdRetryPost,
} from '@closet-theory/api';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, Code, FailureText, Section, StatusBadge } from '@ui/console/primitives';
import { Term } from '@ui/console/glossary';
import { Bar, Quantity, formatCount, formatDuration, plural } from '@ui/console/measures';
import {
  CopyableId,
  EntityRef,
  IdentifierCopy,
  looksLikeUuid,
  refColumnLabel,
  refForField,
} from './references';
import { MetricBand, overLoadedRows, trendFrom } from '@ui/console/MetricBand';
import { SelectField, TextField } from '@ui/console/forms';
import { StructuredValue } from '@ui/console/StructuredValue';
import { formatFull, formatRelative, formatTimestamp, humanize } from '@ui/console/format';
import { configureAdminApi, handleUnauthorized } from './auth';
import { Absent, InsightState, useInsight, wardrobeImportHref } from './consumer-insights';
import { Actions, DetailList, Page } from './components';
import { workflowLabel } from './workflow-labels';
import {
  IoFields,
  StepCardView,
  TechnicalTimeline,
  readSteps,
  type StepCheckpoint,
  type StepLogEntry,
} from './workflow-steps';
import {
  EventCapNote,
  EventSteps,
  EventTimeline,
  eventsCapped,
  eventsOf,
  readEventSteps,
} from './workflow-events';

/** `JobStatus` upstream. Nothing else can appear in `status`. */
export const JOB_STATES = [
  'queued',
  'running',
  'retry_wait',
  'succeeded',
  'dead_lettered',
  'cancelled',
] as const;

/** `limit` is `ge=1, le=200` upstream. */
export const PAGE_SIZE = 50;

/* -------------------------------------------------------------- row reading */

type Row = Record<string, unknown>;

function str(row: Row | undefined, key: string): string | undefined {
  const value = row?.[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function digits(row: Row | undefined, key: string): number | undefined {
  const value = row?.[key];
  return typeof value === 'number' ? value : undefined;
}

function record(value: unknown): Row | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Row) : undefined;
}

function when(value: string | undefined, fallback = 'Not recorded') {
  return value ? formatTimestamp(value) : fallback;
}

function WorkflowFamily({ family }: { family: string | undefined }) {
  if (!family) return <>Unknown</>;
  return (
    <span className="flex min-w-0 flex-col">
      <span>{workflowLabel(family)}</span>
      <span className="font-mono text-xs text-tertiary" title={`Workflow family: ${family}`}>
        {family}
      </span>
    </span>
  );
}

/** `null` inputs/outputs are "not recorded for this run"; `{}` is "recorded, and empty". */
export type Checkpoint = StepCheckpoint;

export function checkpointsOf(job: Row | undefined): Checkpoint[] {
  const raw = job?.['checkpoints'];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => record(entry))
    .filter((entry): entry is Row => Boolean(entry))
    .map((entry) => ({
      sequence: digits(entry, 'sequence') ?? 0,
      attempt: digits(entry, 'attempt') ?? 0,
      checkpoint_type: str(entry, 'checkpoint_type') ?? 'unknown',
      created_at: str(entry, 'created_at'),
      inputs: entry['inputs'] ?? null,
      outputs: entry['outputs'] ?? null,
      output_references: record(entry['output_references']),
    }))
    .sort((left, right) => left.sequence - right.sequence);
}

export function runLogOf(job: Row | undefined): StepLogEntry[] {
  const raw = job?.['logs'];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => record(entry))
    .filter((entry): entry is Row => Boolean(entry))
    .map((entry) => {
      const fields = record(entry['fields']);
      return {
        timestamp: str(entry, 'timestamp'),
        message: str(entry, 'message') ?? '',
        step: str(entry, 'step'),
        fields: fields ?? {},
        error: str(fields, 'error'),
        errorType: str(fields, 'error_type'),
      };
    });
}

/* --------------------------------------------------------------- job list */

/**
 * The state filter keeps each lifecycle word exactly as the queue writes it -
 * an operator searching for `dead_lettered` finds `dead_lettered` - and puts
 * the plain-language meaning after it rather than in place of it.
 */
const STATE_MEANING: Record<(typeof JOB_STATES)[number], string> = {
  queued: 'accepted, waiting for a worker',
  running: 'a worker is working on it now',
  retry_wait: 'it failed and is waiting before trying again',
  succeeded: 'it finished',
  dead_lettered: 'it used up every attempt and stopped',
  cancelled: 'someone stopped it deliberately',
};

const STATE_OPTIONS = [
  { value: '', label: 'All states' },
  ...JOB_STATES.map((state) => ({ value: state, label: `${state} — ${STATE_MEANING[state]}` })),
];

const LIST_COLUMNS = ['job', 'member', 'subject', 'status', 'attempts', 'created'] as const;

type JobSubject = { memberId?: string; subjectId?: string; subjectKind?: 'garment' | 'outfit' };

/**
 * Whose job it is and what it works on. The list returns both: the member the
 * queue recorded for the job (named in the payload, or the owner of its garment
 * or outfit), and the payload's subject. The detail carries the payload itself,
 * read for the member as `member_id`, else `user_id`; subject as `garment_id`,
 * else `outfit_id`.
 */
export function jobSubjectOf(row: Row): JobSubject {
  const payload = record(row['payload']);
  const memberId = str(row, 'member_id') ?? str(payload, 'member_id') ?? str(payload, 'user_id');
  const listedKind = str(row, 'subject_kind');
  const garment = str(payload, 'garment_id');
  const outfit = str(payload, 'outfit_id');
  const subjectId = str(row, 'subject_id') ?? garment ?? outfit;
  const subjectKind =
    listedKind === 'garment' || listedKind === 'outfit'
      ? listedKind
      : garment
        ? 'garment'
        : outfit
          ? 'outfit'
          : undefined;
  return {
    ...(memberId ? { memberId } : {}),
    ...(subjectId && subjectKind ? { subjectId, subjectKind } : {}),
  };
}

function JobMember({ subject }: { subject: JobSubject }) {
  return subject.memberId ? (
    <EntityRef kind="member" id={subject.memberId} hideId />
  ) : (
    <span className="text-quaternary">—</span>
  );
}

function JobSubjectRef({ subject }: { subject: JobSubject }) {
  return subject.subjectId && subject.subjectKind ? (
    <EntityRef
      kind={subject.subjectKind}
      id={subject.subjectId}
      memberId={subject.memberId}
      hideId
    />
  ) : (
    <span className="text-quaternary">—</span>
  );
}

function SparseJobFacts({ row }: { row: Row }) {
  const facts = [
    ['Started', str(row, 'started_at')],
    ['Completed', str(row, 'completed_at')],
    ['Dead-lettered', str(row, 'dead_lettered_at')],
    ['Error', str(row, 'last_error_code')],
  ] as const;
  return (
    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-tertiary">
      {facts.map(([label, value]) =>
        value ? (
          <span key={label}>
            <span className="font-medium">{label}:</span>{' '}
            {label === 'Error' ? <Code>{value}</Code> : formatRelative(value)}
          </span>
        ) : null,
      )}
    </div>
  );
}

/** One field of an expanded job row: refs by name, other uuids as copyable ids. */
function RowValue({ field, value, row }: { field: string; value: unknown; row: Row }) {
  const ref = refForField(field, value, row);
  if (ref) return <EntityRef {...ref} />;
  if (looksLikeUuid(value)) return <CopyableId value={value} label={humanize(field)} full />;
  return <>{formatFull(field, value)}</>;
}

function WorkflowJobsTable({
  rows,
  hideMember = false,
}: {
  rows: readonly Row[];
  /** On a member's own page every row is theirs, so the column says nothing. */
  hideMember?: boolean;
}) {
  const columns = hideMember ? LIST_COLUMNS.filter((column) => column !== 'member') : LIST_COLUMNS;
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <div className="overflow-x-auto rounded-xl border border-secondary bg-primary">
      <table className="w-full min-w-max border-collapse text-left">
        <thead className="bg-secondary">
          <tr>
            {columns.map((column) => (
              <th
                key={column}
                scope="col"
                className="border-b border-secondary px-4 py-2.5 text-xs font-semibold whitespace-nowrap text-tertiary"
              >
                {humanize(column)}
              </th>
            ))}
            <th
              scope="col"
              className="border-b border-secondary px-4 py-2.5 text-xs font-semibold text-tertiary"
            >
              Details
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.flatMap((row, index) => {
            const id = str(row, 'id') ?? `row-${index}`;
            const isExpanded = expanded.has(id);
            const used = digits(row, 'attempt_count') ?? 0;
            const allowed = digits(row, 'max_attempts') ?? 0;
            const created = str(row, 'created_at');
            const subject = jobSubjectOf(row);
            return [
              <tr key={id} className="border-b border-secondary last:border-b-0">
                <td className="max-w-md px-4 py-2.5 align-top text-sm text-secondary">
                  <EntityRef
                    kind="workflow_job"
                    id={id}
                    label={workflowLabel(str(row, 'kind'))}
                    {...(workflowLabel(str(row, 'kind')) !== str(row, 'kind')
                      ? { secondary: str(row, 'kind') }
                      : {})}
                  />
                  <SparseJobFacts row={row} />
                </td>
                {hideMember ? null : (
                  <td className="max-w-xs px-4 py-2.5 align-top text-sm text-secondary">
                    <JobMember subject={subject} />
                  </td>
                )}
                <td className="max-w-xs px-4 py-2.5 align-top text-sm text-secondary">
                  <JobSubjectRef subject={subject} />
                </td>
                <td className="px-4 py-2.5 align-top text-sm text-secondary">
                  <StatusBadge value={str(row, 'status') ?? 'unknown'} />
                </td>
                <td className="px-4 py-2.5 align-top font-mono text-xs text-secondary">
                  {used}/{allowed}
                </td>
                <td className="px-4 py-2.5 align-top text-sm text-secondary">
                  {created ? formatTimestamp(created) : null}
                  {created ? (
                    <span className="block text-xs text-quaternary">{formatRelative(created)}</span>
                  ) : null}
                </td>
                <td className="px-4 py-2.5 align-top">
                  <Button
                    size="sm"
                    color="tertiary"
                    aria-expanded={isExpanded}
                    aria-controls={`workflow-job-${id}-detail`}
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} workflow job ${id}`}
                    onClick={() => toggle(id)}
                  >
                    {isExpanded ? 'Collapse' : 'Expand'}
                  </Button>
                </td>
              </tr>,
              isExpanded ? (
                <tr key={`${id}-detail`} className="border-b border-secondary last:border-b-0">
                  <td colSpan={columns.length + 1} className="p-0">
                    <div
                      id={`workflow-job-${id}-detail`}
                      role="region"
                      aria-label={`Workflow job ${id} expanded details`}
                      className="rounded-xl bg-secondary p-5"
                    >
                      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 xl:grid-cols-3">
                        {Object.entries(row).map(([key, value]) => (
                          <div key={key} className="min-w-0">
                            <dt className="text-xs font-medium text-tertiary uppercase">
                              {key === 'id'
                                ? 'Job reference'
                                : (refColumnLabel(key) ?? humanize(key))}
                            </dt>
                            <dd className="font-mono text-xs break-all whitespace-pre-wrap text-secondary">
                              <RowValue field={key} value={value} row={row} />
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </td>
                </tr>
              ) : null,
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The endpoint pages on `(created_at, id)` with a `before` job id, so there is
 * no offset and no total to show. The cursor stack is the page history: going
 * forward pushes the last id of the page just read, going back pops it. Nothing
 * here reconstructs an offset, which would skip or repeat rows under a queue
 * this busy.
 */
function KeysetPager({
  page,
  atEnd,
  onBack,
  onForward,
}: {
  page: number;
  atEnd: boolean;
  onBack: () => void;
  onForward: () => void;
}) {
  return (
    <nav
      className="flex items-center justify-between gap-3 border-t border-secondary pt-3"
      aria-label="Pagination"
    >
      <span className="text-sm text-tertiary">
        Page {page + 1}, newest first. This list uses <Term term="keyset pagination" />, so it
        cannot tell you how many jobs there are in total.
      </span>
      <div className="flex items-center gap-2">
        <Button size="sm" color="secondary" isDisabled={page === 0} onClick={onBack}>
          Newer
        </Button>
        <Button size="sm" color="secondary" isDisabled={atEnd} onClick={onForward}>
          Older
        </Button>
      </div>
    </nav>
  );
}

export function WorkflowJobs() {
  // Filters live in the URL so a view can be sent to someone else or reloaded
  // without losing what you were looking at.
  const [params, setParams] = useSearchParams();
  const family = params.get('workflow') ?? '';
  const status = params.get('state') ?? '';
  // The head of the stack is the `before` cursor for the page on screen.
  const [cursors, setCursors] = useState<readonly string[]>([]);
  const before = cursors[cursors.length - 1];

  const families = useInsight<string[]>(
    async () => listWorkflowFamiliesApiV1InternalOperatorWorkflowJobsWorkflowsGet({}),
    [],
  );

  const jobs = useInsight<Row[]>(
    async () =>
      listJobsApiV1InternalOperatorWorkflowJobsJobsGet({
        query: {
          limit: PAGE_SIZE,
          ...(family ? { family } : {}),
          ...(status ? { status } : {}),
          ...(before ? { before } : {}),
        },
      }),
    [family, status, before],
  );

  const narrow = (next: { family?: string; status?: string }) => {
    // A cursor belongs to the filter set that produced it.
    setCursors([]);
    const workflow = next.family ?? family;
    const state = next.status ?? status;
    setParams(
      { ...(workflow ? { workflow } : {}), ...(state ? { state } : {}) },
      { replace: true },
    );
  };

  const loaded = jobs.state.status === 'ready' ? jobs.state.data : [];
  // Without the workflow list, offer the kinds the loaded jobs show, plus the
  // one already chosen, so the filter still narrows and never loses its value.
  const kinds =
    families.state.status === 'ready'
      ? families.state.data
      : families.state.status === 'failed'
        ? [
            ...new Set([
              ...(family ? [family] : []),
              ...loaded.map((row) => str(row, 'kind')).filter((kind): kind is string => !!kind),
            ]),
          ].sort()
        : [];
  const familyOptions = [
    { value: '', label: 'All workflows' },
    ...kinds.map((kind) => ({ value: kind, label: `${workflowLabel(kind)} (${kind})` })),
  ];

  const inState = (...wanted: readonly string[]) =>
    loaded.filter((row) => wanted.includes(str(row, 'status') ?? '')).length;
  const running = inState('queued', 'running');
  const waiting = inState('retry_wait');
  const dead = inState('dead_lettered');
  return (
    <Page
      title="Workflow jobs"
      purpose="Use this page to find a piece of background work and see what happened to it — what ran, how far it got, and what it said when it stopped. Open a row for the stage-by-stage detail."
      band={
        <MetricBand
          // Named for the page rather than the queue, because that is what
          // these figures are counted over. Every label below says so too:
          // the caveat belongs on the figure's face, not only in its tooltip,
          // where a figure counted over fifty rows sits beside screens whose
          // bands are counted over a whole table and reads the same.
          label="What this page of the queue is doing"
          figures={[
            {
              id: 'loaded',
              label: 'Jobs loaded',
              value: formatCount(loaded.length),
              unit: plural(loaded.length, 'job'),
              raw: loaded.length,
              hint: (
                <>
                  This list uses <Term term="keyset pagination" />, which stays correct on a table
                  being written to but cannot report a total. The figures beside it count what is on
                  screen, and say so in their labels.
                </>
              ),
            },
            {
              id: 'live',
              label: 'Queued or running, on this page',
              value: formatCount(running),
              unit: plural(running, 'job'),
              raw: running,
              bar: {
                fraction: loaded.length ? running / loaded.length : 0,
                label: `Queued or running: ${running} of ${loaded.length} loaded`,
                tone: 'info',
              },
              hint: 'Work the queue still expects to finish on its own. Nothing here needs a person.',
              tooltip: overLoadedRows(loaded.length),
            },
            {
              id: 'retrying',
              label: (
                <>
                  Waiting on a <Term term="retry_wait">back-off</Term>, on this page
                </>
              ),
              value: formatCount(waiting),
              unit: plural(waiting, 'job'),
              raw: waiting,
              bar: {
                fraction: loaded.length ? waiting / loaded.length : 0,
                label: `Waiting on a retry back-off: ${waiting} of ${loaded.length} loaded`,
                tone: 'warning',
              },
              hint: 'These failed and will try again by themselves. They become dead-lettered only after the last attempt.',
              tooltip: overLoadedRows(loaded.length),
            },
            {
              id: 'dead',
              label: (
                <>
                  <Term term="dead_lettered">Dead-lettered</Term>, on this page
                </>
              ),
              value: formatCount(dead),
              unit: plural(dead, 'job'),
              raw: dead,
              ...(dead > 0 ? { tone: 'error' as const, statusLabel: 'needs a person' } : {}),
              bar: {
                fraction: loaded.length ? dead / loaded.length : 0,
                label: `Dead-lettered: ${dead} of ${loaded.length} loaded`,
                tone: 'error',
              },
              hint: 'Out of attempts and set aside. Open one to read what it died on, and retry it from there.',
              tooltip: overLoadedRows(loaded.length),
            },
          ]}
          trend={{
            points: trendFrom(
              loaded
                .map((row) => new Date(str(row, 'created_at') ?? '').getTime())
                .filter((value) => Number.isFinite(value)),
            ),
            noun: 'jobs',
            caption: 'When the jobs on this page were queued. An idle period keeps its slot.',
          }}
        />
      }
      toolbar={{
        onRefresh: () => void jobs.refresh(),
        filters: (
          <>
            <SelectField
              label="Workflow"
              value={family}
              options={familyOptions}
              onChange={(event) => narrow({ family: event.target.value })}
              hint="Which kind of background work to show. “All workflows” hides nothing."
            />
            <SelectField
              label="State"
              value={status}
              options={STATE_OPTIONS}
              onChange={(event) => narrow({ status: event.target.value })}
              hint="Where a job got to. Each option keeps the exact word the queue uses."
            />
          </>
        ),
      }}
    >
      {families.state.status === 'failed' ? (
        <Callout tone="warning" title="The workflow list could not be read.">
          The workflow filter only offers the kinds of work in the jobs on screen.{' '}
          <FailureText raw={families.state.detail} />
        </Callout>
      ) : null}

      <InsightState source={jobs} subject="the workflow job queue">
        {(rows) =>
          rows.length === 0 ? (
            cursors.length ? (
              // An exactly-full last page leaves Older enabled; this is past it.
              <div className="flex flex-col gap-3">
                <Absent title="No older jobs.">
                  Every job under this workflow and state is on the newer pages.
                </Absent>
                <KeysetPager
                  page={cursors.length}
                  atEnd
                  onBack={() => setCursors((stack) => stack.slice(0, -1))}
                  onForward={() => undefined}
                />
              </div>
            ) : family || status ? (
              <Absent title="No jobs match this workflow and state.">
                Widen either filter back to “All” to see everything.
              </Absent>
            ) : (
              <Absent title="No jobs in the queue.">The queue returned no jobs at all.</Absent>
            )
          ) : (
            <div className="flex flex-col gap-3">
              <WorkflowJobsTable rows={rows} />
              <KeysetPager
                page={cursors.length}
                atEnd={rows.length < PAGE_SIZE}
                onBack={() => setCursors((stack) => stack.slice(0, -1))}
                onForward={() => {
                  const last = str(rows[rows.length - 1], 'id');
                  if (last) setCursors((stack) => [...stack, last]);
                }}
              />
            </div>
          )
        }
      </InsightState>
    </Page>
  );
}

/**
 * One member's workflow runs, for the Workflow runs tab of their page. The
 * queue records which member each job is about - named in its payload, or the
 * owner of the garment or outfit it works on, or the recipient of the
 * notification it sends - so this shows every run that touched them, including
 * garment ingestion, which names only the garment. Bulk imports of several
 * members belong to none of them and appear only on the Workflow jobs page.
 */
export function MemberWorkflowRunsPanel({ id }: { id: string }) {
  const [family, setFamily] = useState('');
  const [status, setStatus] = useState('');
  const [cursors, setCursors] = useState<readonly string[]>([]);
  const before = cursors[cursors.length - 1];

  const families = useInsight<string[]>(
    async () => listWorkflowFamiliesApiV1InternalOperatorWorkflowJobsWorkflowsGet({}),
    [],
  );
  const jobs = useInsight<Row[]>(
    async () =>
      listJobsApiV1InternalOperatorWorkflowJobsJobsGet({
        query: {
          member_id: id,
          limit: PAGE_SIZE,
          ...(family ? { family } : {}),
          ...(status ? { status } : {}),
          ...(before ? { before } : {}),
        },
      }),
    [id, family, status, before],
  );

  const kinds = families.state.status === 'ready' ? families.state.data : family ? [family] : [];
  const familyOptions = [
    { value: '', label: 'All workflows' },
    ...kinds.map((kind) => ({ value: kind, label: `${workflowLabel(kind)} (${kind})` })),
  ];
  const back = () => setCursors((stack) => stack.slice(0, -1));

  return (
    <Section
      title="Workflow runs"
      description="Every piece of background work about this member, newest first: garment processing, outfit images, daily looks, notifications and the rest. Open a run for what it did step by step."
    >
      <div className="flex flex-wrap gap-4">
        <SelectField
          label="Workflow"
          value={family}
          options={familyOptions}
          onChange={(event) => {
            setCursors([]);
            setFamily(event.target.value);
          }}
          hint="Which kind of background work to show."
        />
        <SelectField
          label="State"
          value={status}
          options={STATE_OPTIONS}
          onChange={(event) => {
            setCursors([]);
            setStatus(event.target.value);
          }}
          hint="Where a run got to."
        />
      </div>
      <InsightState source={jobs} subject="this member's workflow runs">
        {(rows) =>
          rows.length === 0 ? (
            cursors.length ? (
              <div className="flex flex-col gap-3">
                <Absent title="No older runs.">
                  Every run under these filters is on newer pages.
                </Absent>
                <KeysetPager
                  page={cursors.length}
                  atEnd
                  onBack={back}
                  onForward={() => undefined}
                />
              </div>
            ) : family || status ? (
              <Absent title="No runs match this workflow and state.">
                Set either filter back to “All” to see every run.
              </Absent>
            ) : (
              <Absent title="No workflow runs for this member.">
                Nothing in the queue is about this member yet.
              </Absent>
            )
          ) : (
            <div className="flex flex-col gap-3">
              <WorkflowJobsTable rows={rows} hideMember />
              <KeysetPager
                page={cursors.length}
                atEnd={rows.length < PAGE_SIZE}
                onBack={back}
                onForward={() => {
                  const last = str(rows[rows.length - 1], 'id');
                  if (last) setCursors((stack) => [...stack, last]);
                }}
              />
            </div>
          )
        }
      </InsightState>
    </Section>
  );
}

/**
 * Attempts used against attempts allowed, as a bar. A job one attempt from
 * giving up and a job that has barely started both read as "2 of 5" until the
 * proportion is drawn.
 */
function AttemptsUsed({ job }: { job: Row }) {
  const used = digits(job, 'attempt_count') ?? 0;
  const allowed = digits(job, 'max_attempts') ?? 0;
  return (
    <span className="flex min-w-0 flex-col gap-1">
      <span>
        <Quantity value={formatCount(used)} raw={used} /> of{' '}
        <Quantity value={formatCount(allowed)} unit="allowed" raw={allowed} />
      </span>
      <Bar
        fraction={allowed > 0 ? used / allowed : 0}
        tone={allowed > 0 && used >= allowed ? 'error' : used > 1 ? 'warning' : 'success'}
        label={`Attempts used: ${used} of ${allowed} allowed`}
      />
    </span>
  );
}

/* ---------------------------------------------------------- pipeline stages */

export type PipelineStage = {
  id: string;
  garmentId: string | undefined;
  stage: string;
  status: string;
  attempt: number | undefined;
  model: string | undefined;
  error: string | undefined;
  durationMs: number | undefined;
  startedAt: string | undefined;
};

/** The garment stage attempts this job ran, as `pipeline_stages` returns them. */
export function pipelineStagesOf(job: Row | undefined): PipelineStage[] {
  const raw = job?.['pipeline_stages'];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => record(entry))
    .filter((entry): entry is Row => Boolean(entry))
    .map((entry, index) => {
      const model = str(entry, 'model');
      const version = str(entry, 'model_version');
      return {
        id: str(entry, 'id') ?? `stage-${index}`,
        garmentId: str(entry, 'garment_id'),
        stage: str(entry, 'stage') ?? 'unknown',
        status: str(entry, 'status') ?? 'unknown',
        attempt: digits(entry, 'attempt'),
        model: model && version ? `${model} (${version})` : model,
        error: str(entry, 'error'),
        durationMs: digits(entry, 'duration_ms'),
        startedAt: str(entry, 'started_at'),
      };
    });
}

const STAGE_COLUMNS = ['Garment', 'Stage', 'Status', 'Attempt', 'Model', 'Error', 'Took'] as const;

function PipelineStagesTable({
  stages,
  memberId,
}: {
  stages: readonly PipelineStage[];
  memberId: string | undefined;
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-secondary bg-primary">
      <table className="w-full min-w-max border-collapse text-left" aria-label="Garment stages">
        <thead className="bg-secondary">
          <tr>
            {STAGE_COLUMNS.map((column) => (
              <th
                key={column}
                scope="col"
                className="border-b border-secondary px-3 py-2 text-xs font-semibold whitespace-nowrap text-tertiary"
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {stages.map((stage) => (
            <tr key={stage.id} className="border-b border-secondary last:border-b-0">
              <td className="px-3 py-2 align-top text-sm">
                {stage.garmentId ? (
                  <EntityRef kind="garment" id={stage.garmentId} memberId={memberId} />
                ) : (
                  <span className="text-quaternary">—</span>
                )}
              </td>
              <td className="px-3 py-2 align-top text-sm text-secondary">
                {humanize(stage.stage)}
              </td>
              <td className="px-3 py-2 align-top">
                <StatusBadge value={stage.status} />
              </td>
              <td className="px-3 py-2 align-top font-mono text-xs text-secondary">
                {stage.attempt ?? '—'}
              </td>
              <td className="px-3 py-2 align-top font-mono text-xs text-secondary">
                {stage.model ?? '—'}
              </td>
              <td className="max-w-xs px-3 py-2 align-top text-xs break-words text-error-primary">
                {stage.error ?? <span className="text-quaternary">—</span>}
              </td>
              <td className="px-3 py-2 align-top text-xs whitespace-nowrap text-secondary">
                {stage.durationMs !== undefined ? formatDuration(stage.durationMs / 1000) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ----------------------------------------------------------------- controls */

type ControlId = 'retry' | 'cancel' | 'resume';

const CONTROLS: Record<
  ControlId,
  { label: string; effect: ReactNode; accepts: ReactNode; reversible: string; reason: boolean }
> = {
  retry: {
    label: 'Retry',
    effect: (
      <>
        Puts a <Term term="dead_lettered" /> job back into the queue, clears its last error and
        grants it further attempts. The work runs again for real: it can call paid AI models, write
        data and send notifications.
      </>
    ),
    accepts: (
      <>
        Only a job in <code>dead_lettered</code>. Anything else is refused with a{' '}
        <Term term="conflict" /> and nothing happens.
      </>
    ),
    reversible:
      'You cannot un-run it, but you can Cancel the new run while it is still queued or waiting.',
    reason: false,
  },
  cancel: {
    label: 'Cancel',
    effect: (
      <>
        Stops a job immediately if it is <code>queued</code> or <code>retry_wait</code>. A job
        already <code>running</code> is only asked to stop — its worker notices the request and
        finishes the job as cancelled itself, so work already done is not undone.
      </>
    ),
    accepts: (
      <>
        Any job that is not already <code>succeeded</code>, <code>dead_lettered</code> or{' '}
        <code>cancelled</code>.
      </>
    ),
    reversible: 'Yes — a cancelled job can be put back with Resume.',
    reason: true,
  },
  resume: {
    label: 'Resume',
    effect: (
      <>
        Puts a job someone cancelled back into the queue and grants it further attempts. The work
        runs again for real: it can call paid AI models, write data and send notifications.
      </>
    ),
    accepts: (
      <>
        Only a job in <code>cancelled</code>. Anything else is refused with a{' '}
        <Term term="conflict" /> and nothing happens.
      </>
    ),
    reversible:
      'You cannot un-run it, but you can Cancel the new run while it is still queued or waiting.',
    reason: false,
  },
};

/**
 * Every one of these runs paid work again or stops it, so none of them fire on
 * a single click: the button opens this panel, the panel says what the call
 * will do, and only Confirm sends it. A failure is shown and left alone —
 * nothing here retries a control call on the operator's behalf.
 */
function ControlPanel({
  jobId,
  control,
  onClose,
  onDone,
}: {
  jobId: string;
  control: ControlId;
  onClose: () => void;
  onDone: () => void;
}) {
  const spec = CONTROLS[control];
  const [reasonCode, setReasonCode] = useState('operator_cancelled');
  const [attempts, setAttempts] = useState('1');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [done, setDone] = useState(false);

  const send = useCallback(async () => {
    setBusy(true);
    setFailure(undefined);
    try {
      configureAdminApi();
      const path = { job_id: jobId };
      const result =
        control === 'cancel'
          ? await cancelJobApiV1InternalOperatorWorkflowJobsJobsJobIdCancelPost({
              path,
              body: { reason_code: reasonCode },
            })
          : control === 'retry'
            ? await retryJobApiV1InternalOperatorWorkflowJobsJobsJobIdRetryPost({
                path,
                // Who asked is recorded from this session by the API.
                body: { additional_attempts: Number(attempts) || 1 },
              })
            : await resumeJobApiV1InternalOperatorWorkflowJobsJobsJobIdResumePost({
                path,
                // Who asked is recorded from this session by the API.
                body: { additional_attempts: Number(attempts) || 1 },
              });
      handleUnauthorized(result.response?.status);
      if (result.response?.status === 409) {
        setFailure('job_state_conflict');
        return;
      }
      if (result.error || !result.data) {
        const detail = (result.error as { detail?: unknown } | undefined)?.detail;
        setFailure(typeof detail === 'string' ? detail : 'control_rejected');
        return;
      }
      setDone(true);
      onDone();
    } catch {
      setFailure('control_unreachable');
    } finally {
      setBusy(false);
    }
  }, [attempts, control, jobId, onDone, reasonCode]);

  if (done)
    return (
      <Callout
        tone="success"
        title={`${spec.label} accepted.`}
        actions={
          <Button size="sm" color="secondary" onClick={onClose}>
            Close
          </Button>
        }
      >
        The job's current state is above, re-read from the queue.
      </Callout>
    );

  return (
    <section
      className="flex flex-col gap-3 rounded-xl border border-secondary bg-primary p-4"
      aria-label={`Confirm ${spec.label.toLowerCase()}`}
    >
      <p className="text-sm font-semibold text-primary">Confirm: {spec.label.toLowerCase()}</p>
      <p className="text-sm text-tertiary">{spec.effect}</p>
      <p className="text-sm text-tertiary">{spec.accepts}</p>
      <p className="text-sm text-tertiary">
        <span className="font-medium">Can this be undone? </span>
        {spec.reversible}
      </p>
      {spec.reason ? (
        <TextField
          label="Reason code"
          hint="A short identifier in lower-case letters, digits and underscores, starting with a letter, such as operator_cancelled. It is stored with the cancellation, together with your account, so keep using the same one for the same situation."
          value={reasonCode}
          onChange={(event) => setReasonCode(event.target.value)}
        />
      ) : (
        <TextField
          label="Additional attempts"
          hint="How many more times the job may try before it gives up again. Between 1 and 20. Your account is recorded as the person who asked."
          type="number"
          min={1}
          max={20}
          value={attempts}
          onChange={(event) => setAttempts(event.target.value)}
        />
      )}
      {failure ? (
        <Callout tone="error" title={`${spec.label} was not applied.`}>
          <FailureText raw={failure} />
        </Callout>
      ) : null}
      <Actions>
        <Button size="sm" color="secondary" onClick={onClose} isDisabled={busy}>
          Cancel
        </Button>
        <Button size="sm" color="primary" onClick={() => void send()} isDisabled={busy}>
          {busy ? 'Sending…' : `Confirm ${spec.label.toLowerCase()}`}
        </Button>
      </Actions>
    </section>
  );
}

/* --------------------------------------------------------------- job detail */

export function WorkflowJobDetail() {
  const params = useParams();
  const jobId = params['jobId'] ?? '';
  const [control, setControl] = useState<ControlId>();

  const job = useInsight<Row>(
    async () => getJobApiV1InternalOperatorWorkflowJobsJobsJobIdGet({ path: { job_id: jobId } }),
    [jobId],
    { pauseRefresh: Boolean(control) },
  );

  return (
    <Page
      title="Workflow job"
      purpose="Use this page to see exactly how far one piece of background work got, what it produced at each stage, and why it stopped — and to run it again or stop it if you need to."
      actions={
        <>
          <Button size="md" color="secondary" onClick={() => void job.refresh()}>
            Refresh
          </Button>
          <Button size="md" color="secondary" href="/workflow-jobs">
            Back to jobs
          </Button>
        </>
      }
    >
      <InsightState source={job} subject="this workflow job">
        {(data) => {
          const status = str(data, 'status');
          const errorCode = str(data, 'last_error_code');
          const checkpoints = checkpointsOf(data);
          const logs = runLogOf(data);
          const payload = record(data['payload']);
          const steps = record(data['steps']) ?? {};
          const startedAt = str(data, 'started_at');
          const completedAt = str(data, 'completed_at');
          const deadLetteredAt = str(data, 'dead_lettered_at');
          const showError = Boolean(errorCode) || (status !== 'queued' && status !== 'succeeded');
          const reading = readSteps({ checkpoints, logs, payload, jobStatus: status });
          // A plain job writes no checkpoints; its event log is then the only
          // record of the stages it went through. A checkpointed job, or an
          // API that returns no events, reads exactly as before.
          const events = checkpoints.length === 0 ? eventsOf(data) : undefined;
          const eventSteps = events?.length
            ? readEventSteps({ events, jobStatus: status, lastErrorCode: errorCode })
            : [];
          const stages = pipelineStagesOf(data);
          const subject = jobSubjectOf(data);
          const stageMember = subject.memberId;
          const givenPayload = payload && Object.keys(payload).length ? payload : undefined;
          const showSteps =
            reading.cards.length > 0 ||
            eventSteps.length > 0 ||
            checkpoints.length > 0 ||
            status !== 'queued';
          const showTimeline = checkpoints.length > 0 || logs.length > 0 || Boolean(events?.length);
          const showResults =
            Object.keys(steps).length > 0 || !['queued', 'running'].includes(status ?? '');
          const importId =
            str(data, 'kind') === 'wardrobe_bulk_import' &&
            typeof payload?.['import_id'] === 'string'
              ? payload['import_id']
              : undefined;
          return (
            <div className="flex flex-col gap-6">
              {importId ? (
                <Actions>
                  <Button size="sm" color="secondary" href={wardrobeImportHref(importId)}>
                    Open import items
                  </Button>
                </Actions>
              ) : null}
              <Section title="Run">
                <DetailList
                  items={[
                    [
                      'Job reference',
                      <IdentifierCopy label="Job" value={str(data, 'id') ?? jobId} />,
                    ],
                    ['Kind of work', <WorkflowFamily family={str(data, 'kind')} />],
                    ['Member', <JobMember subject={subject} />],
                    ['Subject', <JobSubjectRef subject={subject} />],
                    [
                      'Where it got to',
                      <span className="flex items-center gap-2">
                        <StatusBadge value={status ?? 'unknown'} />
                        {status && status in STATE_MEANING ? (
                          <span className="text-xs text-tertiary">
                            {STATE_MEANING[status as (typeof JOB_STATES)[number]]}
                          </span>
                        ) : null}
                      </span>,
                    ],
                    ['Attempts used', <AttemptsUsed job={data} />],
                    ['Queue it ran on', str(data, 'queue_name') ?? 'Not recorded'],
                    ['Resource class', str(data, 'resource_class') ?? 'Not recorded'],
                    ['Handler version', str(data, 'handler_version') ?? 'Not recorded'],
                    ['Created', when(str(data, 'created_at'))],
                    ...(startedAt || status !== 'queued'
                      ? ([['Started', when(startedAt, 'Not started')]] as Array<
                          [ReactNode, ReactNode]
                        >)
                      : []),
                    ...(completedAt || status === 'succeeded'
                      ? ([['Finished', when(completedAt, 'Not finished')]] as Array<
                          [ReactNode, ReactNode]
                        >)
                      : []),
                    ...(deadLetteredAt || status === 'dead_lettered'
                      ? ([
                          [
                            <Term term="dead_lettered" />,
                            when(deadLetteredAt, 'No dead-letter time was recorded'),
                          ],
                        ] as Array<[ReactNode, ReactNode]>)
                      : []),
                  ]}
                />
              </Section>

              {showError ? (
                <Section title="Error" description="The last failure code recorded on this job.">
                  {errorCode ? (
                    <Code>{errorCode}</Code>
                  ) : (
                    <span className="text-sm text-tertiary">No error was recorded.</span>
                  )}
                </Section>
              ) : null}

              <Section
                title="Controls"
                description="These change the live queue. Each one tells you what it will do, and what can be undone, before anything is sent."
              >
                {control ? (
                  <ControlPanel
                    jobId={jobId}
                    control={control}
                    onClose={() => setControl(undefined)}
                    onDone={() => void job.refresh()}
                  />
                ) : (
                  <Actions>
                    <Button size="sm" color="secondary" onClick={() => setControl('retry')}>
                      Retry
                    </Button>
                    <Button size="sm" color="secondary" onClick={() => setControl('resume')}>
                      Resume
                    </Button>
                    <Button
                      size="sm"
                      color="primary-destructive"
                      onClick={() => setControl('cancel')}
                    >
                      Cancel
                    </Button>
                  </Actions>
                )}
              </Section>

              {showSteps ? (
                <Section
                  title="Steps"
                  description={
                    eventSteps.length && !reading.cards.length
                      ? 'One card for each stage the job reported, in order — when it started, how long it took, and where it stopped.'
                      : 'One card for each step the workflow ran, in the order they finished — what went in, what came out, and what the step said. A step that was retried has a card for each attempt.'
                  }
                >
                  {!reading.cards.length && eventSteps.length ? (
                    <div className="flex flex-col gap-3">
                      {givenPayload ? (
                        <div className="flex flex-col gap-2 rounded-xl border border-secondary bg-primary p-4">
                          <h3 className="text-xs font-semibold tracking-wide text-tertiary uppercase">
                            What the job was given
                          </h3>
                          <IoFields value={givenPayload} jobId={jobId} />
                        </div>
                      ) : null}
                      {eventsCapped(events) ? <EventCapNote /> : null}
                      <EventSteps steps={eventSteps} />
                    </div>
                  ) : reading.cards.length ? (
                    <ol className="m-0 flex list-none flex-col gap-3 p-0">
                      {reading.cards.map((card) => (
                        <StepCardView
                          key={card.key}
                          card={card}
                          jobId={jobId}
                          logsCapped={reading.logsCapped}
                          siblings={reading.cards}
                          memberId={stageMember}
                        />
                      ))}
                    </ol>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <Absent title="This job has no workflow steps to show.">
                        Either it has not reached its first step yet, or it is a kind of work that
                        does not report steps. The technical timeline below has everything it did
                        record.
                      </Absent>
                      {givenPayload ? (
                        <div className="flex flex-col gap-2 rounded-xl border border-secondary bg-primary p-4">
                          <h3 className="text-xs font-semibold tracking-wide text-tertiary uppercase">
                            What the job was given
                          </h3>
                          <IoFields value={givenPayload} jobId={jobId} />
                        </div>
                      ) : null}
                    </div>
                  )}
                </Section>
              ) : null}

              {stages.length ? (
                <Section
                  title="Garment stages"
                  description="Each garment processing stage this job ran, one row per attempt, oldest first."
                >
                  <PipelineStagesTable stages={stages} memberId={stageMember} />
                </Section>
              ) : null}

              {showResults ? (
                <Section
                  title="Completed step results"
                  description="The accumulated results a resume would skip re-running."
                >
                  {Object.keys(steps).length ? (
                    <StructuredValue value={steps} />
                  ) : (
                    <Absent title="No step has completed on this run.">
                      Either it has not started a step yet, or its first step is the one that broke.
                    </Absent>
                  )}
                </Section>
              ) : null}

              {showTimeline ? (
                <Section
                  title="Technical timeline"
                  description="Every record the worker wrote for this job, in order, including the bookkeeping around the steps. For debugging; the steps above are the same run, read plainly."
                >
                  {events?.length ? (
                    <EventTimeline
                      events={events}
                      logs={reading.unmatched}
                      logsCapped={reading.logsCapped}
                    />
                  ) : (
                    <TechnicalTimeline checkpoints={checkpoints} reading={reading} />
                  )}
                </Section>
              ) : null}
            </div>
          );
        }}
      </InsightState>
    </Page>
  );
}
